/**
 * Whether a catalog product is in pre-order, for the chip on the product browser card
 * (flag `preorder_button`). Mirrors the server derivation in `src/utils/preorderState.ts`
 * for display. A stored `lifecycle` (flag `preorder_lifecycle`, row B7) is read first: its
 * stage is the answer, with a release date that has arrived meaning on sale. Without one:
 * the US date, then `global`, then `launchDate`; a date that has arrived is on sale; with no
 * date an announced product or a manufacturer `preorder` status is pre-order. The sync itself
 * uses the store's own region and time zone; this chip is a hint, not what is written.
 */
interface PreorderProductShape {
  launchDate?: string | null;
  launchDates?: { announced?: string; us?: { date?: string }; global?: { date?: string } } | null;
  manufacturerStock?: { status?: string } | null;
  lifecycle?: { stage?: string; releaseDate?: string | null } | null;
}

const dayOf = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

export function isProductInPreorder(product: PreorderProductShape, now: Date = new Date()): boolean {
  const today = now.toISOString().slice(0, 10);
  const lifecycle = product.lifecycle;
  if (lifecycle && (lifecycle.stage === 'announced' || lifecycle.stage === 'preorder' || lifecycle.stage === 'released')) {
    const release = dayOf(lifecycle.releaseDate);
    if (release) return release > today;
    return lifecycle.stage !== 'released';
  }
  const ld = product.launchDates;
  const day = dayOf(ld?.us?.date) ?? dayOf(ld?.global?.date) ?? dayOf(product.launchDate);
  if (day) return day > today;
  return Boolean(ld?.announced) || product.manufacturerStock?.status === 'preorder';
}
